import { useState } from 'react';
import { Link } from 'wouter';
import { RefreshCw, Plus, AlertCircle, FileText } from 'lucide-react';
import { useGetArtefacts, useGetProjects } from '@edison/api-client-react';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { Card } from '@/components/ui/card.js';
import { formatLabel } from '@/lib/utils.js';

const ARTEFACT_TYPES = [
  { value: '', label: 'All types' },
  { value: 'github_commit', label: 'GitHub commit' },
  { value: 'github_pr', label: 'GitHub PR' },
  { value: 'github_issue', label: 'GitHub issue' },
  { value: 'meeting_transcript', label: 'Meeting transcript' },
  { value: 'prd_document', label: 'PRD document' },
  { value: 'llm_session', label: 'LLM session' },
  { value: 'abandoned_work', label: 'Abandoned work' },
  { value: 'market_search', label: 'Market search' },
  { value: 'manual_note', label: 'Manual note' },
  { value: 'file_upload', label: 'File upload' },
];

export function ArtefactListPage() {
  const [typeFilter, setTypeFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');

  const { data: projectsData } = useGetProjects();
  const projects = projectsData?.data?.items ?? [];
  const projectById = new Map(projects.map((p) => [p.id, p.name]));

  const params: Record<string, string> = {};
  if (typeFilter) params['type'] = typeFilter;
  if (projectFilter) params['projectId'] = projectFilter;

  const { data, isLoading, error, refetch } = useGetArtefacts(
    Object.keys(params).length ? params : undefined
  );

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-16 rounded-lg skeleton" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Failed to load evidence vault.</span>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </button>
      </div>
    );
  }

  const items = data?.data?.items ?? [];
  const hasFilters = typeFilter || projectFilter;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Evidence Vault</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Evidence artefacts captured and stored securely for R&D claims
          </p>
        </div>
        <Link href="/artefacts/new">
          <Button variant="primary">
            <Plus className="h-4 w-4" />
            New artefact
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="mb-4 space-y-3">
        {/* Project filter */}
        {projects.length > 0 && (
          <div className="flex gap-2 flex-wrap items-center">
            <span className="text-xs text-muted-foreground font-medium">Project:</span>
            <button
              onClick={() => setProjectFilter('')}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                projectFilter === ''
                  ? 'bg-primary text-primary-foreground'
                  : 'border hover:bg-accent text-muted-foreground'
              }`}
            >
              All projects
            </button>
            {projects.map((p) => (
              <button
                key={p.id}
                onClick={() => setProjectFilter(p.id)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  projectFilter === p.id
                    ? 'bg-primary text-primary-foreground'
                    : 'border hover:bg-accent text-muted-foreground'
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
        )}

        {/* Type filter */}
        <div className="flex gap-2 flex-wrap">
          {ARTEFACT_TYPES.map((t) => (
            <button
              key={t.value}
              onClick={() => setTypeFilter(t.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                typeFilter === t.value
                  ? 'bg-secondary text-secondary-foreground'
                  : 'border hover:bg-accent text-muted-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <FileText className="mx-auto h-10 w-10 text-muted-foreground/50" />
          {hasFilters ? (
            <>
              <p className="mt-4 text-muted-foreground">No artefacts match these filters.</p>
              <button
                onClick={() => { setTypeFilter(''); setProjectFilter(''); }}
                className="mt-2 text-sm text-primary hover:underline"
              >
                Clear filters
              </button>
            </>
          ) : (
            <>
              <p className="mt-4 text-muted-foreground">No artefacts yet.</p>
              <p className="text-sm text-muted-foreground mt-1">
                Create a manual note or connect a signal source to begin.
              </p>
              <Link href="/artefacts/new">
                <Button variant="primary" className="mt-4">
                  <Plus className="h-4 w-4" />
                  Create first artefact
                </Button>
              </Link>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((a) => (
            <Link key={a.id} href={`/artefacts/${a.id}`}>
              <Card variant="interactive">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <span className="text-sm font-medium truncate block">{a.title ?? '—'}</span>
                  </div>
                  <Badge variant="default" className="shrink-0">{formatLabel(a.type)}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(a.createdAt).toLocaleDateString('en-AU', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                  {a.projectId && (
                    <span className="ml-2 text-muted-foreground/60">
                      · {projectById.get(a.projectId) ?? 'linked to project'}
                    </span>
                  )}
                </p>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
